import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { notify } from '../../../notifications';
import { apiErrorMessage } from '../../reports/edit/editApi';
import {
  createColumn,
  deleteColumn,
  fetchOperation,
  fetchRevenue,
  releaseLock,
  startRecalc,
  syncColumn,
  updateColumn
} from './revenueApi';
import type { OperationJob, OperationState, RevenueColumn, RevenueDraft, RevenueList } from './revenueApi';
import { finishedNote, isBusy, pollDelay, POLL_IDLE_MS } from './revenueOps';

export type RevenueAction = 'save' | 'delete' | 'sync' | 'recalc' | 'unlock';

/** Результат действия: текст для уведомления и, если работа ушла в фон, операция для отслеживания */
interface Outcome {
  message: string;
  warning?: string | null;
  operation?: OperationJob | null;
  /** Как повторить операцию, если она завершится не полностью */
  retry?: () => Promise<boolean>;
}

interface RunOptions {
  /** Перечитать список колонок после действия (по умолчанию да) */
  reload?: boolean;
}

/**
 * Данные экрана «Таблица выручки»: колонки, их изменение и длительные операции над таблицами аптек.
 * watchOperations: у пользователя есть право видеть состояние операций, иначе опрос не ведется.
 */
export function useRevenue(watchOperations: boolean) {
  const [data, setData] = useState<RevenueList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pending, setPending] = useState<RevenueAction | null>(null);
  const [operation, setOperation] = useState<OperationState | null>(null);
  const [hiddenJob, setHiddenJob] = useState('');
  const [retries, setRetries] = useState<Record<string, () => Promise<boolean>>>({});

  const requestRef = useRef(0);
  const pendingRef = useRef(false);
  // Операция, за ходом которой следим: когда она завершится, сообщаем об этом и перечитываем колонки
  const trackedRef = useRef<{ id: string; running: boolean } | null>(null);
  const wakeRef = useRef<() => void>(() => undefined);

  /** keepRows: после действия на экране остаются прежние строки (приглушенные), а не пустой экран */
  const load = useCallback(async (keepRows: boolean) => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setLoading(true);
    setError('');
    if (!keepRows) setData(null);

    try {
      const list = await fetchRevenue();
      // Устаревший ответ (экран закрыт или список уже перечитан) отбрасывается
      if (requestId !== requestRef.current) return;
      setData(list);
    } catch (err) {
      if (requestId !== requestRef.current) return;
      const reason = apiErrorMessage(err, 'Проверьте соединение и повторите попытку');
      if (keepRows) notify(`Не удалось обновить колонки таблицы. ${reason}`, 'error');
      else setError(reason);
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(false);
  }, [load]);

  useEffect(
    () => () => {
      requestRef.current += 1;
    },
    []
  );

  const retry = useCallback(() => {
    load(false);
  }, [load]);

  // ---------------------------------------------------------------------------
  // Опрос состояния операции
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!watchOperations) return undefined;

    let cancelled = false;
    let inflight = false;
    let timer = 0;

    const schedule = (delay: number) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(poll, delay);
    };

    /** Операция, которую мы видели идущей, завершилась или пропала: сообщаем итог и обновляем колонки */
    const settle = (next: OperationState) => {
      const tracked = trackedRef.current;
      if (!tracked || !tracked.running) return;
      const job = next.job;
      if (job && job.id === tracked.id) {
        if (job.status === 'running') return;
        trackedRef.current = { id: job.id, running: false };
        const note = finishedNote(job);
        notify(note.text, note.severity);
      } else if (!next.lock) {
        // Сервер перезапускался: итог неизвестен, но состояние таблиц надо перечитать
        trackedRef.current = null;
      } else {
        return;
      }
      load(true);
    };

    async function poll() {
      if (cancelled || inflight) return;
      // Скрытая вкладка не опрашивает сервер: опрос возобновится при возвращении на нее
      if (document.hidden) return;
      inflight = true;
      try {
        const next = await fetchOperation();
        if (cancelled) return;
        setOperation(next);
        settle(next);
        schedule(pollDelay(next));
      } catch (err) {
        // Сбой опроса не ломает экран: просто повторим позже
        if (!cancelled) schedule(POLL_IDLE_MS);
      } finally {
        inflight = false;
      }
    }

    const handleVisibility = () => {
      if (!document.hidden) schedule(0);
    };

    wakeRef.current = () => schedule(0);
    document.addEventListener('visibilitychange', handleVisibility);
    schedule(0);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', handleVisibility);
      wakeRef.current = () => undefined;
    };
  }, [watchOperations, load]);

  /** Начинает следить за операцией, запущенной на этом экране */
  const adopt = useCallback((job: OperationJob, retryFn?: () => Promise<boolean>) => {
    trackedRef.current = { id: job.id, running: job.status === 'running' };
    setHiddenJob('');
    setOperation((prev) => ({ lock: prev ? prev.lock : null, job }));
    if (retryFn) setRetries((prev) => ({ ...prev, [job.id]: retryFn }));
    wakeRef.current();
  }, []);

  // ---------------------------------------------------------------------------
  // Действия
  // ---------------------------------------------------------------------------

  /** Выполняет действие и перечитывает список: после сбоя состояние на сервере могло измениться */
  const run = useCallback(
    async (action: RevenueAction, task: () => Promise<Outcome>, failText: string, options?: RunOptions): Promise<boolean> => {
      if (pendingRef.current) return false;
      pendingRef.current = true;
      setPending(action);

      let done = false;
      try {
        const outcome = await task();
        notify(outcome.message, outcome.operation ? 'info' : 'success');
        if (outcome.warning) notify(outcome.warning, 'warning');
        if (outcome.operation) adopt(outcome.operation, outcome.retry);
        done = true;
      } catch (err) {
        notify(apiErrorMessage(err, failText), 'error');
        // Отказ мог прийти из-за блокировки данных: покажем ее на панели сразу
        wakeRef.current();
      } finally {
        pendingRef.current = false;
        setPending(null);
      }

      if (!options || options.reload !== false) load(true);
      return done;
    },
    [load, adopt]
  );

  const startCreate = useCallback(
    function start(draft: RevenueDraft): Promise<boolean> {
      return run('save', async () => {
        const created = await createColumn(draft);
        return {
          message: created.operation
            ? created.message || 'Колонка добавляется в таблицы аптек'
            : `Колонка «${draft.displayName}» добавлена`,
          warning: created.warning,
          operation: created.operation,
          retry: () => start(draft)
        };
      }, 'Не удалось добавить колонку');
    },
    [run]
  );

  /** target: редактируемая колонка или null для новой */
  const saveColumn = useCallback(
    (target: RevenueColumn | null, draft: RevenueDraft) => {
      if (!target) return startCreate(draft);
      return run('save', async () => {
        await updateColumn(target.id, draft);
        return { message: `Колонка «${draft.displayName}» обновлена` };
      }, 'Не удалось сохранить колонку');
    },
    [run, startCreate]
  );

  const removeColumn = useCallback(
    (column: RevenueColumn) =>
      run('delete', async () => {
        await deleteColumn(column.id);
        return { message: `Колонка «${column.displayName.trim()}» удалена` };
      }, 'Не удалось удалить колонку'),
    [run]
  );

  /** Добавляет колонку в таблицы аптек, где ее нет */
  const startSync = useCallback(
    function start(column: RevenueColumn): Promise<boolean> {
      return run('sync', async () => {
        const started = await syncColumn(column.id);
        return {
          message: started.message || 'Колонка уже есть во всех таблицах аптек',
          operation: started.operation,
          retry: () => start(column)
        };
      }, 'Не удалось запустить добавление колонки');
    },
    [run]
  );

  /** Пересчитывает историю расчетных полей с даты from (YYYY-MM-DD) */
  const startRecalcFrom = useCallback(
    function start(from: string): Promise<boolean> {
      return run('recalc', async () => {
        const started = await startRecalc(from);
        return {
          message: started.message || 'Пересчет истории запущен',
          operation: started.operation,
          retry: () => start(from)
        };
      }, 'Не удалось запустить пересчет истории', { reload: false });
    },
    [run]
  );

  const releaseDataLock = useCallback(
    () =>
      run('unlock', async () => ({ message: await releaseLock() }), 'Не удалось снять блокировку', { reload: false }),
    [run]
  );

  // ---------------------------------------------------------------------------
  // Панель операции
  // ---------------------------------------------------------------------------

  const job = operation ? operation.job : null;
  // Завершенную операцию можно убрать с экрана; идущую - нет
  const shownJob = job && !(job.status !== 'running' && job.id === hiddenJob) ? job : null;
  const lock = operation ? operation.lock : null;
  const shown = useMemo<OperationState | null>(
    () => (operation ? { lock, job: shownJob } : null),
    [operation, lock, shownJob]
  );

  const dismissJob = useCallback(() => {
    if (job && job.status !== 'running') setHiddenJob(job.id);
  }, [job]);

  /** Повторить завершившуюся не до конца операцию можно, пока экран помнит, как ее запустили */
  const retryJob = shownJob && shownJob.status === 'incomplete' && retries[shownJob.id] ? retries[shownJob.id] : null;
  const retryFromHistory =
    !retryJob && shownJob && shownJob.status === 'incomplete' && shownJob.op === 'recalc' && shownJob.result && shownJob.result.from
      ? shownJob.result.from
      : null;

  const repeatJob = useCallback((): Promise<boolean> => {
    if (retryJob) return retryJob();
    if (retryFromHistory) return startRecalcFrom(retryFromHistory);
    return Promise.resolve(false);
  }, [retryJob, retryFromHistory, startRecalcFrom]);

  return {
    data,
    loading,
    error,
    retry,
    pending,
    operation: shown,
    busy: isBusy(shown),
    canRepeat: !!retryJob || !!retryFromHistory,
    repeatJob,
    dismissJob,
    saveColumn,
    removeColumn,
    startSync,
    startRecalcFrom,
    releaseDataLock
  };
}
